/**
 * Move or copy between My Files and a folder someone shares with you (E8, P1-F4).
 *
 * Two people's spaces are involved, so the browser can't do it with either one's storage calls:
 * the sharing API's transfer endpoint checks access, sizes the work (a dry run first, shown here),
 * and a worker copies in the background while the client polls. A move deletes the originals only
 * after every copy succeeded.
 */
import React, {createContext, useContext, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useQuery} from '@tanstack/react-query'
import {Id, toast} from 'react-toastify'
import {toastActionableConfig, toastDefaultConfig} from '@/shared/config/toastConfig'
import {formatBytes} from '@/shared/utils/SystemHelpers'
import {getLogger} from '@/shared/utils/logUtils'
import {SHARED_FOLDER_ICON} from './constants'
import {shareErrorMessage} from './errors'
import {
  dryRunShareTransfer,
  SHARE_TRANSFER_MAX_BYTES,
  SHARE_TRANSFER_MAX_OBJECTS,
  ShareTransferMode,
  ShareTransferRequest,
  startShareTransfer,
} from './shareFilesApi'
import {
  operationPathFrom,
  ShareOperationError,
  shareOperationErrorMessage,
  waitForShareOperation,
} from './shareOperations'
import './sharing.scss'

/** The shared folder on the other side of the transfer. */
export interface ShareTransferShare {
  shareId: string
  name: string
  /** Its path in the owner's space, ending in `/` (to show its keys relative to it). */
  folderPath: string
  ownerName?: string
}

/** An item to transfer, relative to the side it is on. Folders end in `/`. */
export interface ShareTransferSource {
  path: string
  name: string
  isDir: boolean
}

export type ShareTransferDirection = 'toShare' | 'toMine'

/** What the user asked to transfer, and where to. */
export interface ShareTransferIntent {
  /** `toShare`: from My Files into the shared folder; `toMine`: the other way. */
  direction: ShareTransferDirection
  /** The shared folder. For `toShare` it may be left to the user, from {@link shareChoices}. */
  share?: ShareTransferShare
  /** For `toShare`: the shared folders (Can edit) the user can choose from. */
  shareChoices?: ShareTransferShare[]
  sources: ShareTransferSource[]
  /** The destination folder: `''` for the top of My Files or of the shared folder, else `a/b/`. */
  destination: string
  /** Move unless said otherwise (E8): Copy when the user copied (Ctrl+C) before pasting. */
  defaultMode?: ShareTransferMode
  /** When Move can't be offered (E10: a source is a folder the user shares), why. */
  moveBlockedReason?: string
}

export interface ShareTransferOutcome {
  intent: ShareTransferIntent
  share: ShareTransferShare
  mode: ShareTransferMode
  status: 'succeeded' | 'failed'
}

const MAX_LISTED = 5
const MAX_CONFLICTS_LISTED = 10

const quoted = (name: string) => `“${name}”`

/** `a/b/` as `My Files › a › b`, or the shared folder's name and its subfolders. */
function placeLabel(top: string, folder: string): string {
  const parts = folder.split('/').filter(Boolean)
  return [top, ...parts].join(' › ')
}

const itemsLabel = (count: number) => (count === 1 ? '1 item' : `${count.toLocaleString()} items`)

/** The request for an intent, against `share`. */
function transferRequest(
  intent: ShareTransferIntent,
  mode: ShareTransferMode
): ShareTransferRequest {
  return {
    mode,
    from: intent.direction === 'toShare' ? 'mine' : 'share',
    to: intent.direction === 'toShare' ? 'share' : 'mine',
    sources: intent.sources.map((s) => s.path),
    destination: intent.destination,
  }
}

/** Short names for a list of keys, at most {@link MAX_LISTED}, then "and N more". */
function listNames(keys: string[], base: string): string {
  const names = keys.map((key) => operationPathFrom(key, base))
  const shown = names.slice(0, MAX_LISTED).join(', ')
  return names.length > MAX_LISTED ? `${shown} and ${names.length - MAX_LISTED} more` : shown
}

const ShareTransferContext = createContext<((intent: ShareTransferIntent) => void) | null>(null)

/** Opens the Move/Copy dialog, or null outside a {@link ShareTransferProvider}. */
export function useOpenShareTransfer(): ((intent: ShareTransferIntent) => void) | null {
  return useContext(ShareTransferContext)
}

/**
 * Holds the Move/Copy dialog for a Files browser. `onFinished` runs when a transfer ends (it may
 * be long after the dialog closed), so the browser can refresh both sides.
 */
export const ShareTransferProvider: React.FC<{
  children: React.ReactNode
  onFinished?: (outcome: ShareTransferOutcome) => void
}> = ({children, onFinished}) => {
  const [intent, setIntent] = useState<ShareTransferIntent | null>(null)
  const onFinishedRef = useRef(onFinished)
  onFinishedRef.current = onFinished
  return (
    <ShareTransferContext.Provider value={setIntent}>
      {children}
      {intent && (
        <ShareTransferDialog
          intent={intent}
          onHide={() => setIntent(null)}
          onFinished={(outcome) => onFinishedRef.current?.(outcome)}
        />
      )}
    </ShareTransferContext.Provider>
  )
}

export interface ShareTransferDialogProps {
  intent: ShareTransferIntent
  onHide: () => void
  onFinished?: (outcome: ShareTransferOutcome) => void
}

/**
 * The dialog: where from and to, Move (the default) or Copy, and what the dry run found (size,
 * count, items that would be replaced), with the limits. Running it closes the dialog; progress,
 * success and failure follow in a toast.
 */
export const ShareTransferDialog: React.FC<ShareTransferDialogProps> = ({
  intent,
  onHide,
  onFinished,
}) => {
  const choices = useMemo(
    () => intent.shareChoices ?? (intent.share ? [intent.share] : []),
    [intent.shareChoices, intent.share]
  )
  const [shareId, setShareId] = useState(intent.share?.shareId ?? choices[0]?.shareId ?? '')
  const share = choices.find((c) => c.shareId === shareId) ?? intent.share
  const moveAllowed = !intent.moveBlockedReason
  const [mode, setMode] = useState<ShareTransferMode>(
    moveAllowed ? intent.defaultMode ?? 'move' : 'copy'
  )
  const toShare = intent.direction === 'toShare'

  // Sized once per destination: the count, size and conflicts don't depend on the mode.
  const planRequest = useMemo(() => transferRequest(intent, 'copy'), [intent])
  const plan = useQuery({
    queryKey: ['sharing', 'transfer-plan', shareId, planRequest],
    queryFn: () => dryRunShareTransfer(shareId, planRequest),
    enabled: !!shareId,
    retry: false,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  })

  const shareName = share ? quoted(share.name) : 'the shared folder'
  const from = toShare ? 'My Files' : shareName
  const to = toShare
    ? placeLabel(share ? quoted(share.name) : 'the shared folder', intent.destination)
    : placeLabel('My Files', intent.destination)
  const what =
    intent.sources.length === 1 ? quoted(intent.sources[0].name) : itemsLabel(intent.sources.length)

  // Conflicts come relative to the owner's space; inside the shared folder, show them relative to it.
  const conflictBase = toShare && share ? share.folderPath : ''
  const conflicts = plan.data?.conflicts ?? []

  const run = () => {
    if (!share || !plan.isSuccess) return
    onHide()
    void runTransfer({
      intent,
      share,
      mode,
      objects: plan.data.objects,
      onFinished,
    })
  }

  return (
    <Modal
      show
      onHide={onHide}
      centered
      aria-labelledby='share-transfer-title'
      dialogClassName='ob-sharing-dialog'
      data-testid='share-transfer-dialog'
    >
      <Modal.Header closeButton>
        <Modal.Title id='share-transfer-title' className='text-truncate'>
          <i className={`${SHARED_FOLDER_ICON} me-2`} aria-hidden='true' />
          Move or copy {what}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <dl className='ob-sharing-transfer__route mb-3'>
          <dt>From</dt>
          <dd data-testid='share-transfer-from'>{from}</dd>
          <dt>To</dt>
          <dd data-testid='share-transfer-to'>
            {toShare && choices.length > 1 ? (
              <select
                className='form-select form-select-sm'
                aria-label='Shared folder'
                value={shareId}
                onChange={(e) => setShareId(e.target.value)}
                data-testid='share-transfer-share'
              >
                {choices.map((choice) => (
                  <option key={choice.shareId} value={choice.shareId}>
                    {choice.name}
                    {choice.ownerName ? ` (${choice.ownerName})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              to
            )}
          </dd>
        </dl>

        <fieldset className='mb-3'>
          <legend className='form-label fs-6 mb-2'>What to do</legend>
          <div className='form-check mb-2'>
            <input
              className='form-check-input'
              type='radio'
              name='share-transfer-mode'
              id='share-transfer-move'
              checked={mode === 'move'}
              disabled={!moveAllowed}
              onChange={() => setMode('move')}
            />
            <label className='form-check-label' htmlFor='share-transfer-move'>
              Move
              <span className='d-block text-muted small'>
                {!moveAllowed
                  ? intent.moveBlockedReason
                  : toShare
                  ? "They leave My Files. In the shared folder they count toward its owner's storage."
                  : 'They leave the shared folder, so its other members lose them, and count toward your storage.'}
              </span>
            </label>
          </div>
          <div className='form-check'>
            <input
              className='form-check-input'
              type='radio'
              name='share-transfer-mode'
              id='share-transfer-copy'
              checked={mode === 'copy'}
              onChange={() => setMode('copy')}
            />
            <label className='form-check-label' htmlFor='share-transfer-copy'>
              Copy
              <span className='d-block text-muted small' data-testid='share-transfer-copy-note'>
                A copy doubles the storage these files use:{' '}
                {toShare
                  ? "the originals stay in your My Files, and the copies count toward the shared folder owner's storage."
                  : 'the originals stay in the shared folder, and the copies count toward your storage.'}
              </span>
            </label>
          </div>
        </fieldset>

        <div className='mb-2' data-testid='share-transfer-plan' aria-live='polite'>
          {!shareId ? (
            <span className='text-muted'>No shared folder to move to.</span>
          ) : plan.isLoading ? (
            <span className='text-muted'>
              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
              Checking what will be transferred…
            </span>
          ) : plan.isError ? (
            <div className='alert alert-danger small mb-0' role='alert'>
              {shareErrorMessage(plan.error)}
            </div>
          ) : plan.data ? (
            <strong>
              {itemsLabel(plan.data.objects)}, {formatBytes(plan.data.bytes, 1)}
            </strong>
          ) : null}
        </div>

        {conflicts.length > 0 && (
          <div className='alert alert-warning small mb-2' data-testid='share-transfer-conflicts'>
            {conflicts.length === 1
              ? '1 item already exists there and will be replaced:'
              : `${conflicts.length}${
                  conflicts.length >= 50 ? ' or more' : ''
                } items already exist there and will be replaced:`}
            <ul className='mb-0 mt-1'>
              {conflicts.slice(0, MAX_CONFLICTS_LISTED).map((key) => (
                <li key={key} className='text-break'>
                  {operationPathFrom(key, conflictBase)}
                </li>
              ))}
              {conflicts.length > MAX_CONFLICTS_LISTED && (
                <li>and {conflicts.length - MAX_CONFLICTS_LISTED} more</li>
              )}
            </ul>
          </div>
        )}

        <p className='text-muted small mb-0' data-testid='share-transfer-limit'>
          At most {SHARE_TRANSFER_MAX_OBJECTS.toLocaleString()} files or{' '}
          {formatBytes(SHARE_TRANSFER_MAX_BYTES, 0)} per transfer. It runs in the background; you
          can keep working.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onHide}>
          Cancel
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={run}
          disabled={!share || !plan.isSuccess}
          data-testid='share-transfer-run'
        >
          {mode === 'move' ? 'Move' : 'Copy'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

const VERBS: Record<ShareTransferMode, {ing: string; ed: string; base: string}> = {
  move: {ing: 'Moving', ed: 'Moved', base: 'move'},
  copy: {ing: 'Copying', ed: 'Copied', base: 'copy'},
}

/** Starts the transfer and follows it in a toast, then reports to `onFinished`. */
export async function runTransfer({
  intent,
  share,
  mode,
  objects,
  onFinished,
}: {
  intent: ShareTransferIntent
  share: ShareTransferShare
  mode: ShareTransferMode
  objects: number
  onFinished?: (outcome: ShareTransferOutcome) => void
}): Promise<void> {
  const verb = VERBS[mode]
  const where = intent.direction === 'toShare' ? quoted(share.name) : 'My Files'
  const toastId: Id = toast.loading(`${verb.ing} ${itemsLabel(objects)} to ${where}…`, {
    ...toastDefaultConfig,
    autoClose: false,
    closeOnClick: false,
  })
  // Keys from the operation are relative to their owner's space.
  const fromBase = intent.direction === 'toMine' ? share.folderPath : ''
  const toBase = intent.direction === 'toShare' ? share.folderPath : ''
  let status: ShareTransferOutcome['status'] = 'failed'
  try {
    const started = await startShareTransfer(share.shareId, transferRequest(intent, mode))
    const total = started.objects || objects
    await waitForShareOperation(share.shareId, started.operationId, {
      onProgress: (op) =>
        toast.update(toastId, {
          render: `${verb.ing} to ${where}… ${(op.done ?? 0).toLocaleString()} of ${(
            op.objects ?? total
          ).toLocaleString()}`,
        }),
    })
    status = 'succeeded'
    toast.update(toastId, {
      render: `${verb.ed} ${itemsLabel(total)} to ${where}.`,
      type: 'success',
      isLoading: false,
      ...toastDefaultConfig,
    })
  } catch (error) {
    getLogger().warn('Shared folder transfer failed', error)
    const op = error instanceof ShareOperationError ? error.operation : undefined
    toast.update(toastId, {
      render: (
        <div data-testid='share-transfer-failed'>
          <div className='fw-bold'>
            Couldn't {verb.base} the items to {where}.
          </div>
          <div>{shareOperationErrorMessage(error)}</div>
          {op?.failed && op.failed.length > 0 && (
            <div className='mt-1'>Not transferred: {listNames(op.failed, fromBase)}</div>
          )}
          {op?.copied && op.copied.length > 0 && (
            <div className='mt-1'>
              {mode === 'move' ? 'Nothing was deleted. Already copied: ' : 'Copied: '}
              {listNames(op.copied, toBase)}
            </div>
          )}
        </div>
      ),
      type: 'error',
      isLoading: false,
      ...toastActionableConfig,
    })
  } finally {
    onFinished?.({intent, share, mode, status})
  }
}
